import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CREATING,
  fetchEligibleBenchmarkOptions,
  fetchNotionalLimitEligibility,
  fetchMacroBackground,
  HISTORICAL_SCENARIO,
  retrieveUnderlyingAssets,
  selectors,
} from 'state';

import '../SessionSettingsShared/settings_studio.css';
import './dense.css';
import {
  isSessionLocked,
  useFillHeight,
} from '../SessionSettingsShared/helpers';
import { useBenchmarkEditOverride } from '../SessionSettingsShared/useBenchmarkEditOverride';
import { SettingsLockNudgeProvider } from '../SessionSettingsShared/SettingsLockNudge';
import TopBar from '../SessionSettingsShared/components/TopBar';
import SettingsCard from '../SessionSettingsShared/components/SettingsCard';
import { TrainerSessionContext } from '../TrainerSessionContext';
import { InstrumentSelectionContext } from '../SessionSettings/instrumentSelectionContext';
import DenseRail, { DenseView } from './components/DenseRail';
import BenchmarkViewV2 from './benchmark_v2/BenchmarkViewV2';

// Reused group editor (verbatim wiring) from the rail+workspace redesign.
import MacroBackground from '../MacroBackground';
import GeneralViewV2 from './general_v2/GeneralViewV2';
import InstrumentsPanelV2 from '../../../features/assetCatalogue/SessionInstrumentsSwitch';
import { SessionCatalogueProvider } from '../../../features/assetCatalogue/SessionCatalogue';
import { useNotionalLimitDraft } from './instruments_v2/useNotionalLimitDraft';

/** Instrument universe — the virtualized two-table selector inline, filling the
 *  workspace. */
const InstrumentsViewV2: React.FC<{
  readOnly?: boolean;
  includeSavedSelection: boolean;
}> = ({ readOnly, includeSavedSelection }) => (
  <div className="set3b-instruments">
    <InstrumentsPanelV2
      readOnly={readOnly}
      includeSavedSelection={includeSavedSelection}
    />
  </div>
);

/** Macro Background — the existing editor (fetch on mount + save) inline. */
const MacroView: React.FC = () => (
  <div className="set3b-macro">
    <SettingsCard title="Macro Background" scroll>
      <MacroBackground />
    </SettingsCard>
  </div>
);

/** Benchmark & Alpha monitoring — the redesigned BenchmarkViewV2 workspace
 *  (type selector · library · composition builder · constant · live preview ·
 *  alpha monitoring). Just computes the session lock and hands off. Alpha
 *  monitoring inside is historical-only. */
const BenchmarkView: React.FC<{ historical: boolean }> = ({ historical }) => {
  // Benchmark settings lock once the session starts, like the other structural
  // settings — but a trainer/admin can keep them editable for this page load by
  // appending the secret `?unlockBenchmark` param (frontend-only, not persisted).
  // Per-sector alpha is NOT gated here: AlphaMonitoringV2 owns its own status
  // lock, so it doesn't need the gate.
  const session = useSelector(selectors.activeSession);
  const benchmarkEditOverride = useBenchmarkEditOverride();
  const benchmarkLocked = isSessionLocked(session) && !benchmarkEditOverride;

  return <BenchmarkViewV2 historical={historical} locked={benchmarkLocked} />;
};

/**
 * Settings3 — dense two-view variant. A slim rail with exactly two options
 * (General Settings · Markets & Benchmarks). General is a fitted, non-scrolling
 * dashboard; Markets shows the data-heavy editors inline (no drawers). Reuses
 * the rail+workspace redesign's panels + redux wiring verbatim.
 */
const SessionSettingsDense: React.FC<{
  blueprintMode?: boolean;
  // Read-only view (e.g. a locked blueprint version): the workspace content is non-interactive, but the
  // left rail stays navigable so every settings page can still be opened and viewed.
  readOnly?: boolean;
}> = ({ blueprintMode, readOnly }) => {
  const dispatch = useDispatch();
  const { ref, height } = useFillHeight();
  useContext(TrainerSessionContext); // keep provider satisfied for reused children

  const session = useSelector(selectors.activeSession);
  const klass = useSelector(selectors.klassById(session?.klass));
  const historical = session.scenario_type === HISTORICAL_SCENARIO;

  const [view, setView] = useState<DenseView>('general');

  // One controller owns the live draft, serialized persistence and refresh
  // recovery. Execution-time V1/V2 evaluators are deliberately not involved.
  const draftHydrationBaseline = useRef({
    sessionId: session.id,
    version: session.notional_limit_settings_hydration_version ?? 0,
  });
  if (draftHydrationBaseline.current.sessionId !== session.id) {
    draftHydrationBaseline.current = {
      sessionId: session.id,
      version: session.notional_limit_settings_hydration_version ?? 0,
    };
  }
  const instrumentLimitDraft = useNotionalLimitDraft(
    session,
    draftHydrationBaseline.current.version + 1,
  );
  const instrumentSelection = {
    ...instrumentLimitDraft,
    selected:
      !blueprintMode && session.status !== CREATING
        ? session.underlying_assets || []
        : instrumentLimitDraft.selected,
    autoSaveDraft: true,
    reloadDraft: () =>
      dispatch(fetchNotionalLimitEligibility({ session_id: session.id })),
  };

  // Same shared data deps the other shells own.
  useEffect(() => {
    dispatch(retrieveUnderlyingAssets({}));
  }, [dispatch]);
  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchEligibleBenchmarkOptions({ session_id: session.id }));
  }, [dispatch, session?.id]);
  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchNotionalLimitEligibility({ session_id: session.id }));
  }, [dispatch, session?.id, session?.scenario_type]);
  // Lift the macro-briefing fetch up to the shell so the rail's "Briefing set /
  // No briefing yet" flag is accurate without first opening the Macro tab.
  // Guarded by fetched_macro_background so it runs once; the Macro tab's own
  // effect already skips re-fetching (and its loading screen) once this has run.
  useEffect(() => {
    if (!session?.id || session.fetched_macro_background) return;
    dispatch(fetchMacroBackground({ session_id: session.id }));
  }, [dispatch, session?.id, session?.fetched_macro_background]);

  return (
    <SessionCatalogueProvider
      sessionId={
        !blueprintMode && session.status !== CREATING ? session.id : undefined
      }
      selectedIds={session.underlying_assets}
    >
      <InstrumentSelectionContext.Provider value={instrumentSelection}>
        <SettingsLockNudgeProvider>
          <div ref={ref} className="set-studio set3" style={{ height }}>
            <TopBar
              session={session}
              klassName={klass?.name}
              hideStartButton={blueprintMode}
              selectedAssetCount={instrumentLimitDraft.selected.length}
              instrumentDraftReady={instrumentLimitDraft.draftReady}
              instrumentDraftDirty={instrumentLimitDraft.draftDirty}
              instrumentDraftSaving={instrumentLimitDraft.draftSaving}
              instrumentDraftError={instrumentLimitDraft.draftError}
              instrumentLimitLaunchReady={instrumentLimitDraft.draftLaunchReady}
            />
            <div className="set-studio__body">
              <DenseRail active={view} onSelect={setView} session={session} />
              <div className="set-studio__workspace">
                <div
                  className="set-studio__panel"
                  // The instruments view manages its own read-only state (selection disabled, but columns
                  // stay sortable/filterable), so it's excluded from the blunt pointer-events overlay used
                  // by the other views.
                  style={
                    readOnly && view !== 'instruments'
                      ? { pointerEvents: 'none', opacity: 0.6 }
                      : undefined
                  }
                  aria-disabled={readOnly}
                >
                  {view === 'general' ? (
                    <GeneralViewV2 blueprintMode={blueprintMode} />
                  ) : view === 'instruments' ? (
                    <InstrumentsViewV2
                      readOnly={readOnly}
                      includeSavedSelection={!blueprintMode}
                    />
                  ) : view === 'macro' ? (
                    <MacroView />
                  ) : (
                    <BenchmarkView historical={historical} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </SettingsLockNudgeProvider>
      </InstrumentSelectionContext.Provider>
    </SessionCatalogueProvider>
  );
};

export default SessionSettingsDense;
